import React, { useEffect, useState } from 'react';
import Link from '../common/Link';
import LineChart from '../common/LineChart';
import { ReactComponent as ArrowLeft } from 'assets/img/arrow-left.svg';
import InputSelectTract from '../common/InputSelectTract';
import ChartLegend from './ChartLegend';

export default function CompareTractSidePanel({
  children,
  activeTract = null,
  activeCompareSimilarTract = null,
  socioeconomicBroadbandDataTractA = null,
  socioeconomicBroadbandPredictionsDataTractA = null,
  socioeconomicBroadbandDataTractB = null,
  socioeconomicBroadbandPredictionsDataTractB = null,
  features = null,
  socioeconomicFactor = null,
  backLinkAction,
}) {
  const [dataChart1, setDataChart1] = useState(null);
  const [chartLegendItems, setChartLegendItems] = useState([]);
  const [previousSocioeconomicFactor, setPreviousSocioeconomicFactor] = useState(null);
  const [previousActiveTract, setPreviousActiveTract] = useState(null);
  const [previousActiveCompareSimilarTract, setPreviousActiveCompareSimilarTract] =
    useState(null);

  let socioeconomicFactorLabel = findFeatureLabel(features, socioeconomicFactor);

  useEffect(() => {
    if (
      !socioeconomicBroadbandDataTractA ||
      !socioeconomicBroadbandPredictionsDataTractA ||
      !socioeconomicBroadbandDataTractB ||
      !socioeconomicBroadbandPredictionsDataTractB ||
      !activeCompareSimilarTract ||
      !activeTract
    ) {
      return;
    }

    if (
      socioeconomicFactor in socioeconomicBroadbandDataTractA?.[0] === false ||
      socioeconomicFactor in socioeconomicBroadbandDataTractB?.[0] === false
    ) {
      return;
    }

    if (
      activeTract === previousActiveTract &&
      activeCompareSimilarTract === previousActiveCompareSimilarTract &&
      socioeconomicFactor === previousSocioeconomicFactor
    ) {
      return;
    }

    let socioeconomicBroadbandChartLabels = [1, 2, 3, 4, 5];

    // Tract A data wranging.
    let socioeconomicBroadbandChartData1TractA = socioeconomicBroadbandChartLabels.map(
      (index) =>
        socioeconomicBroadbandDataTractA.find((item) => item.broadband === index)?.[
          socioeconomicFactor
        ]
    );
    let socioeconomicBroadbandChartData2TractA = socioeconomicBroadbandChartLabels.map(
      (index) => {
        return socioeconomicBroadbandPredictionsDataTractA.find(
          (item) => item.broadband === index
        )?.[socioeconomicFactor];
      }
    );
    const socioeconomicBroadbandChartDataMergedTractA =
      socioeconomicBroadbandChartData1TractA.map((value, index) =>
        isNaN(value) ? socioeconomicBroadbandChartData2TractA[index] : value
      );
    const skippedTractA = (ctx, value) =>
      ctx.p0.skip ||
      ctx.p1.skip ||
      isNaN(socioeconomicBroadbandChartData1TractA[ctx.p1DataIndex])
        ? value
        : undefined;

    // Tract B data wranging.
    let socioeconomicBroadbandChartData1TractB = socioeconomicBroadbandChartLabels.map(
      (index) =>
        socioeconomicBroadbandDataTractB.find((item) => item.broadband === index)?.[
          socioeconomicFactor
        ]
    );

    let socioeconomicBroadbandChartData2TractB = socioeconomicBroadbandChartLabels.map(
      (index) => {
        return socioeconomicBroadbandPredictionsDataTractB.find(
          (item) => item.broadband === index
        )?.[socioeconomicFactor];
      }
    );
    const socioeconomicBroadbandChartDataMergedTractB =
      socioeconomicBroadbandChartData1TractB.map((value, index) =>
        isNaN(value) ? socioeconomicBroadbandChartData2TractB[index] : value
      );

    const skippedTractB = (ctx, value) =>
      ctx.p0.skip ||
      ctx.p1.skip ||
      isNaN(socioeconomicBroadbandChartData1TractB[ctx.p1DataIndex])
        ? value
        : undefined;

    setDataChart1({
      labels: socioeconomicBroadbandChartLabels,
      datasets: [
        {
          data: socioeconomicBroadbandChartDataMergedTractA,
          borderColor: '#99d292',
          hoverBorderColor: '#3FB02D',
          fill: false,
          tension: 0.5,
          spanGaps: false,
          elements: {
            point: {
              backgroundColor: (ctx) => {
                return isStandaloneDataPoint(
                  socioeconomicBroadbandChartData1TractA,
                  ctx.dataIndex
                )
                  ? '#99d292'
                  : 'transparent';
              },
              hoverBackgroundColor: (ctx) => {
                if (
                  isNaN(socioeconomicBroadbandChartData1TractA[ctx.dataIndex]) === false
                ) {
                  return '#06315D';
                }

                return isStandaloneDataPoint(
                  socioeconomicBroadbandChartData1TractA,
                  ctx.dataIndex
                )
                  ? '#06315D'
                  : 'transparent';
              },
              radius: (ctx) => {
                return isStandaloneDataPoint(
                  socioeconomicBroadbandChartData1TractA,
                  ctx.dataIndex
                )
                  ? 3
                  : 5;
              },
            },
          },
          segment: {
            borderColor: (ctx) => skippedTractA(ctx, '#99d292'),
            borderDash: (ctx) => skippedTractA(ctx, [6, 6]),
            borderWidth: (ctx) => skippedTractA(ctx, 2),
          },
        },
        {
          data: socioeconomicBroadbandChartDataMergedTractB,
          hoverBorderColor: function (context) {
            const chart = context.chart;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              // This case happens on initial chart load
              return;
            }

            return getGradient(ctx, chartArea);
          },
          borderColor: '#d676c2',
          fill: false,
          tension: 0.5,
          spanGaps: false,
          elements: {
            point: {
              backgroundColor: (ctx) => {
                return isStandaloneDataPoint(
                  socioeconomicBroadbandChartData1TractB,
                  ctx.dataIndex
                )
                  ? '#d676c2'
                  : 'transparent';
              },
              hoverBackgroundColor: (ctx) => {
                if (
                  isNaN(socioeconomicBroadbandChartData1TractB[ctx.dataIndex]) === false
                ) {
                  return '#06315D';
                }

                return isStandaloneDataPoint(
                  socioeconomicBroadbandChartData1TractB,
                  ctx.dataIndex
                )
                  ? '#06315D'
                  : 'transparent';
              },
              radius: (ctx) => {
                return isStandaloneDataPoint(
                  socioeconomicBroadbandChartData1TractB,
                  ctx.dataIndex
                )
                  ? 3
                  : 5;
              },
            },
          },
          segment: {
            borderColor: (ctx) => skippedTractB(ctx, '#d676c2'),
            borderDash: (ctx) => skippedTractB(ctx, [6, 6]),
            borderWidth: (ctx) => skippedTractB(ctx, 2),
          },
        },
      ],
    });

    // Filter out predictions for which actual data is already available.
    const predictionsTractAFiltered = socioeconomicBroadbandChartData2TractA.filter(
      (_, i) => isNaN(socioeconomicBroadbandChartData1TractA[i])
    );
    const predictionsTractBFiltered = socioeconomicBroadbandChartData2TractB.filter(
      (_, i) => isNaN(socioeconomicBroadbandChartData1TractB[i])
    );

    // Set chart 1 legend items.
    const chartLegendItems = [];

    if (
      isArrayEmpty(socioeconomicBroadbandChartData1TractA) === false ||
      isArrayEmpty(socioeconomicBroadbandChartData1TractB) === false
    ) {
      chartLegendItems.push({ value: 'blue', label: 'Observation' });
    }
    if (
      isArrayEmpty(predictionsTractAFiltered) === false ||
      isArrayEmpty(predictionsTractBFiltered) === false
    ) {
      chartLegendItems.push({ value: 'dashed', label: 'Prediction' });
    }

    setChartLegendItems(chartLegendItems);
    setPreviousActiveTract(activeTract);
    setPreviousSocioeconomicFactor(socioeconomicFactor);
    setPreviousActiveCompareSimilarTract(activeCompareSimilarTract);
  }, [
    activeCompareSimilarTract,
    activeTract,
    previousActiveCompareSimilarTract,
    previousActiveTract,
    previousSocioeconomicFactor,
    socioeconomicBroadbandDataTractA,
    socioeconomicBroadbandDataTractB,
    socioeconomicBroadbandPredictionsDataTractA,
    socioeconomicBroadbandPredictionsDataTractB,
    socioeconomicFactor,
  ]);

  function isStandaloneDataPoint(arr, index) {
    if (index === 0) {
      return isNaN(arr[1]) && !isNaN(arr[0]);
    } else if (index === arr.length - 1) {
      return isNaN(arr[arr.length - 2]) && !isNaN(arr[arr.length - 1]);
    } else {
      return isNaN(arr[index - 1]) && isNaN(arr[index + 1]) && !isNaN(arr[index]);
    }
  }

  let getGradient = function (ctx, chartArea) {
    let gradient;

    gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
    gradient.addColorStop(0, '#D64CE9');
    gradient.addColorStop(1, '#CB41AC');

    return gradient;
  };

  return (
    <div data-component-name='CompareTractSidePanel' className='h-full'>
      <div className='flex flex-col h-full text-blue-600'>
        {backLinkAction && (
          <Link
            action={backLinkAction}
            icon={<ArrowLeft />}
            isIconLeft={true}
            color='text-blue-500/60'
          >
            <span className='text-base -translate-y-px'>back</span>
          </Link>
        )}

        <h2 className='pb-4 mt-4 text-lg font-bold'>Compare similar tracts</h2>

        <div className='h-full overflow-auto pb-7'>
          <div className='rounded-lg bg-blue-50'>
            <div className='grid grid-cols-2 gap-4 px-4 pt-4 4'>
              <div className='flex flex-col gap-2'>
                <span className='text-xs text-blue-300'>Tract A</span>
                <InputSelectTract
                  disabled={true}
                  activeTract={activeTract}
                  color='green'
                />
              </div>

              <div className='flex flex-col gap-2'>
                <span className='text-xs text-blue-300'>Tract B</span>
                <InputSelectTract
                  disabled={true}
                  activeTract={activeCompareSimilarTract}
                  color='pink'
                  // handleUpdateActiveCompareTract={handleUpdateActiveCompareTract}
                  // searchQuery={searchQuery}
                  // searchResults={searchResults}
                  // setSearchQuery={setSearchQuery}
                  // isSearchResultsLoading={isSearchResultsLoading}
                  // updateSelected={updateSelected}
                />
              </div>
            </div>
            <LineChart
              data={dataChart1}
              labelY={<span>{socioeconomicFactorLabel}</span>}
              labelX={<span className='pl-3'>Broadband score</span>}
            >
              <ChartLegend items={chartLegendItems} />
            </LineChart>
          </div>
        </div>

        {/* <h3 className='text-sm font-semibold mt-7'>
          {socioeconomicFactorLabel} over the years
        </h3>
        <div className='mt-3 '>
          <LineChart
            data={dataChart2}
            labelY={<span>{socioeconomicFactorLabel}</span>}
            labelX={<span className='pl-3'>Years</span>}
          />
        </div> */}
      </div>
      {children}
    </div>
  );
}

const findFeatureLabel = function (features, input) {
  if (features === null) {
    return;
  }

  const categoryMatch =
    features.find((categoryData) => categoryData.category === input) || null;

  if (categoryMatch !== null) {
    return categoryMatch.category;
  }

  const featureMatch = features
    .flatMap((categoryData) => categoryData.features)
    .find((featureData) => featureData.id === input);

  return featureMatch ? featureMatch.label : null;
};

const isArrayEmpty = function (array) {
  // Check if all elements in the array are not a number.
  return array.every((element) => isNaN(element) === true);
};
